import { useEffect, useCallback, useSyncExternalStore } from "react";
import { estimatePositionFromEnvironment } from "@/lib/geoCache";


export interface GeoPosition {
  lat: number;
  lng: number;
}

interface UseGeolocationReturn {
  position: GeoPosition | null;
  loading: boolean;
  error: string | null;
  /** true when the position is a stale cache hit or a city-level estimate. */
  approximate: boolean;
  requestLocation: () => void;
}


/* ------------------------------------------------------------------ *
 * Global location store — a single detected position is shared by
 * every map, listing and proximity badge in the app so they all agree
 * on "where the user is".
 * ------------------------------------------------------------------ */

const CACHE_KEY = "gloogum:geo:last";
const CONSENT_KEY = "gloogum:geo:consented";
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

interface GeoState {
  position: GeoPosition | null;
  loading: boolean;
  error: string | null;
  detectedAt: number | null;
  /** true when the position is a stale cache hit or a city-level estimate. */
  approximate: boolean;
}


function readCache(): { position: GeoPosition; detectedAt: number } | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.position?.lat === "number" &&
      typeof parsed?.position?.lng === "number" &&
      typeof parsed?.detectedAt === "number"
    ) {
      return parsed;
    }
  } catch { /* ignore */ }
  return null;
}

const cached = typeof window !== "undefined" ? readCache() : null;

let state: GeoState = {
  position: cached?.position ?? null,
  loading: false,
  error: null,
  detectedAt: cached?.detectedAt ?? null,
  approximate: cached ? Date.now() - cached.detectedAt > CACHE_TTL : false,
};


const listeners = new Set<() => void>();

function setState(patch: Partial<GeoState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

function getSnapshot() {
  return state;
}

let inFlight = false;

/**
 * Fallback origin used when precise geolocation is unavailable or denied:
 * 1. last known position (even if stale), 2. a city-level estimate from the
 * browser timezone. Never overwrites a precise position.
 */
export function applyFallbackLocation(reason?: string): void {
  if (typeof window === "undefined") return;
  if (state.position && !state.approximate) return;

  const stale = readCache();
  if (stale?.position) {
    setState({
      position: stale.position,
      detectedAt: stale.detectedAt,
      approximate: true,
      loading: false,
      error: reason ?? state.error,
    });
    return;
  }

  const est = estimatePositionFromEnvironment();
  if (est) {
    setState({
      position: { lat: est.lat, lng: est.lng },
      detectedAt: Date.now(),
      approximate: true,
      loading: false,
      error: reason ?? state.error,
    });
    return;
  }

  setState({ loading: false, error: reason ?? state.error });
}

/** Trigger a browser geolocation lookup shared by the whole app. */
export function detectLocation(force = false): void {
  if (typeof window === "undefined") return;
  if (inFlight) return;
  if (!force && state.position && !state.approximate && state.detectedAt && Date.now() - state.detectedAt < CACHE_TTL) return;
  if (!navigator.geolocation) {
    applyFallbackLocation("Geolocation is not supported by your browser");
    return;
  }
  inFlight = true;
  setState({ loading: true, error: null });
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      inFlight = false;
      const position = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      const detectedAt = Date.now();
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ position, detectedAt }));
        localStorage.setItem(CONSENT_KEY, "1");
      } catch { /* ignore */ }
      setState({ position, detectedAt, loading: false, error: null, approximate: false });
    },
    (err) => {
      inFlight = false;
      // Permission denied / timeout / unavailable — keep the app usable.
      applyFallbackLocation(err.message);
    },
    { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
  );
}

/**
 * Silent app-wide bootstrap: on every page load / refresh we re-detect the
 * position (fresh, ignoring the cache) whenever permission is already
 * granted, so every map, listing and distance badge reflects where the
 * user actually is right now. Never prompts unless previously consented.
 */
export async function bootstrapLocation(force = true): Promise<void> {
  if (typeof window === "undefined") return;
  if (!navigator.geolocation) { applyFallbackLocation("Geolocation is not supported by your browser"); return; }
  try {
    const consented = localStorage.getItem(CONSENT_KEY) === "1";
    const perms = (navigator as any).permissions;
    if (perms?.query) {
      const status = await perms.query({ name: "geolocation" as PermissionName });
      if (status.state === "granted") {
        detectLocation(force);
        status.onchange = () => { if (status.state === "granted") detectLocation(true); };
        return;
      }
      if (status.state === "denied") {
        applyFallbackLocation("Location permission denied — using your last known area");
        return;
      }
      status.onchange = () => { if (status.state === "granted") detectLocation(true); };
    }
    if (consented) detectLocation(force);
    else applyFallbackLocation();
  } catch { applyFallbackLocation(); }
}


/**
 * Shared geolocation hook. Every consumer reads the same global position,
 * so maps, listings and distance badges stay in sync.
 */
export function useGeolocation(autoDetect = false): UseGeolocationReturn {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const requestLocation = useCallback(() => detectLocation(true), []);

  useEffect(() => {
    if (autoDetect) detectLocation();
  }, [autoDetect]);

  return {
    position: snap.position,
    loading: snap.loading,
    error: snap.error,
    approximate: snap.approximate,
    requestLocation,
  };

}

/**
 * Map centering helper: returns the user's position when known,
 * otherwise the provided fallback center.
 */
export function useMapCenter(
  fallback: [number, number],
  fallbackZoom = 2,
): { center: [number, number]; zoom: number; hasUserPosition: boolean } {
  const { position } = useGeolocation(true);
  const hasUserPosition = !!position;
  return {
    center: position ? [position.lat, position.lng] : fallback,
    zoom: position ? Math.max(fallbackZoom, 11) : fallbackZoom,
    hasUserPosition,
  };
}

/** Haversine distance in miles between two lat/lng points */
export function haversineDistance(a: GeoPosition, b: GeoPosition): number {
  const R = 3958.8; // Earth radius in miles
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const c =
    sinDLat * sinDLat +
    Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * sinDLng * sinDLng;
  return R * 2 * Math.atan2(Math.sqrt(c), Math.sqrt(1 - c));
}

export type MapApp = "google" | "waze" | "apple" | "default";
const MAP_PREF_KEY = "gloogum:map-pref";

/** Read the user's preferred map app from localStorage. */
export function getMapPreference(): MapApp {
  try {
    const v = localStorage.getItem(MAP_PREF_KEY);
    if (v === "google" || v === "waze" || v === "apple" || v === "default") return v;
  } catch { /* ignore */ }
  return "default";
}

/** Persist the user's preferred map app. */
export function setMapPreference(app: MapApp) {
  try { localStorage.setItem(MAP_PREF_KEY, app); } catch { /* ignore */ }
}

function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && (navigator as any).maxTouchPoints > 1);
}

/**
 * Open location in an external map app.
 * When `app` is "default" (or omitted), the user's saved preference is used,
 * falling back to Apple Maps on iOS, otherwise Google Maps.
 */
export function openInExternalMap(lat: number, lng: number, label?: string, app: MapApp = "default") {
  const encodedLabel = encodeURIComponent(label || "Location");
  let target: MapApp = app;
  if (target === "default") {
    const pref = getMapPreference();
    target = pref === "default" ? (isIOS() ? "apple" : "google") : pref;
  }
  switch (target) {
    case "waze":
      window.open(`https://www.waze.com/ul?ll=${lat},${lng}&navigate=yes`, "_blank");
      break;
    case "apple":
      window.open(`https://maps.apple.com/?ll=${lat},${lng}&q=${encodedLabel}`, "_blank");
      break;
    case "google":
    default:
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, "_blank");
  }
}
